import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { DatePicker, Button } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { EntReservoirHistoryFilter } from '../../../../../models/filters/reservoirHistoryFilter';
import { useGetByIdReservoirQuery } from '../../api';
import style from './style.module.css';
import { useNavigate } from 'react-router-dom';
import { PreloaderSvg } from '../../../../preloaderSvg/PreloaderSvg';

const { RangePicker } = DatePicker;

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface Reservoir {
  id: number;
  nameRu: string;
  nameKy: string;
  yearOfConstruction: string;
  designCapacity: number;
  polygonJson: string;
  createdTime: string;
  editedTime: string | null;
  deleted: boolean;
}

interface ReservoirHistoryItem {
  id: number;
  dateForDay: string;
  descriptions: {
    volume: number;
    admission: number;
    workOff: number;
    reservoir: Reservoir;
  };
  deleted: boolean;
  editedTime: string | null;
  createdTime: string | null;
}

interface ReservoirHistoryResponse {
  success: boolean;
  msg: string | null;
  res: ReservoirHistoryItem[];
  time: string;
  ver: string;
}

interface ReservoirDetailsProps {
  filter: EntReservoirHistoryFilter;
}

const ReservoirStatistics: React.FC<ReservoirDetailsProps> = ({ filter }) => {
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(14, 'day'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());
  const updatedFilter = {
    ...filter,
    dateForDayFrom: startDate.format('YYYY-MM-DD'),
    dateForDayTo: endDate.format('YYYY-MM-DD')
  };
  const { data, error, isLoading } = useGetByIdReservoirQuery(updatedFilter);
  const [chartData, setChartData] = useState<any>(null);
  const [reservoirName, setReservoirName] = useState<string>('');
  const navigate = useNavigate();
  const handleGoBack = () => {
    navigate(-1); 
  };

  useEffect(() => {
    if (data && Array.isArray(data.res)) {
      const name = data.res[0]?.descriptions?.reservoir?.nameRu || 'Неизвестно';
      setReservoirName(name);

      const filteredData = data.res.filter(
        (item: ReservoirHistoryItem) =>
          item.dateForDay >= startDate.format('YYYY-MM-DD') &&
          item.dateForDay <= endDate.format('YYYY-MM-DD')
      );

      const chartDataFiltered = filteredData.map((item: ReservoirHistoryItem) => ({
        date: item.dateForDay,
        volume: item.descriptions.volume,
        admission: item.descriptions.admission,
        workOff: item.descriptions.workOff,
      }));

      setChartData({
        volume: {
          labels: chartDataFiltered.map(item => item.date).reverse(),
          datasets: [
            {
              label: 'Объём',
              data: chartDataFiltered.map(item => item.volume),
              borderColor: 'blue',
              backgroundColor: 'rgba(0, 0, 255, 0.2)',
              fill: true,
              tension: 0.4,
              borderWidth: 2,
            },
          ],
        },
        admission: {
          labels: chartDataFiltered.map(item => item.date).reverse(),
          datasets: [
            {
              label: 'Поступление',
              data: chartDataFiltered.map(item => item.admission),
              borderColor: 'green',
              backgroundColor: 'rgba(0, 255, 0, 0.2)',
              fill: true,
              tension: 0.4,
              borderWidth: 2,
            },
          ],
        },
        workOff: {
          labels: chartDataFiltered.map(item => item.date).reverse(),
          datasets: [
            {
              label: 'Сработка',
              data: chartDataFiltered.map(item => item.workOff),
              borderColor: 'red',
              backgroundColor: 'rgba(255, 0, 0, 0.2)',
              fill: true,
              tension: 0.4,
              borderWidth: 2,
            },
          ],
        },
      });
    }
  }, [data, startDate, endDate]);

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates.length === 2) {
      setStartDate(dates[0]);
      setEndDate(dates[1]);
    }
  };

  const shiftDateRange = (days: number) => {
    setStartDate(prev => prev.add(days, 'day'));
    setEndDate(prev => prev.add(days, 'day'));
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <PreloaderSvg />
      </div>
    );
  }
  if (error) return <div>Error: {JSON.stringify(error)}</div>;

  return (
    <div className={style.tabble_main}>
      <Button className={style.arrow_back_main} onClick={handleGoBack} type="primary">
        <img className={style.arrow_back} src="/Arrow-Left.png" alt="назад" />
        Назад
      </Button>
      <div className={style.tabble_main_inner}>
        <h1>Статистика объема, сработки и поступления для {reservoirName}</h1>
        <div className={style.controller_island}>
          <div className={style.date_controls}>
            <DatePicker 
              value={startDate}
              onChange={(date) => handleDateRangeChange([date, endDate])}
              placeholder="Start Date"
            />
            <DatePicker 
              value={endDate}
              onChange={(date) => handleDateRangeChange([startDate, date])}
              placeholder="End Date"
            />
          </div>
          <div className={style.shift_buttons}>
            <Button className={style.button} onClick={() => shiftDateRange(-10)}>
              -10 
            </Button>
            <Button className={style.button} onClick={() => shiftDateRange(10)}>
              +10
            </Button>
          </div>
        </div>

        <div style={{ marginBottom: '20px', width: '80%' }}>
          <h2>Статистика по Объёму</h2>
          {chartData?.volume && (
            <Line data={chartData.volume} options={{ responsive: true, scales: { y: { min: 0 } } }} />
          )}
        </div>
        <div style={{ marginBottom: '20px', width: '80%' }}>
          <h2>Статистика по Поступлению</h2>
          {chartData?.admission && (
            <Line data={chartData.admission} options={{ responsive: true, scales: { y: { min: 0 } } }} />
          )}
        </div>
        <div style={{ marginBottom: '20px', width: '80%' }}>
          <h2>Статистика по Сработке</h2>
          {chartData?.workOff && (
            <Line data={chartData.workOff} options={{ responsive: true, scales: { y: { min: 0 } } }} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ReservoirStatistics;
